'use client';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import Title from '@/components/common/ui/text/Title';
import LargeHeading from '@/components/common/ui/text/LargeHeading';
import AwardSingle from '@/components/common/ui/single-item/AwardSingle';
import { StyledPartners } from '@/components/common/ygen/sections/partners/partners.style';
import { useInView } from 'framer-motion';
import { CSSPlugin, gsap } from 'gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { CSSRulePlugin } from 'gsap/dist/CSSRulePlugin';
import { useShowSetting } from '@/core/context/ShowSettingProvider';
import { Autoplay, FreeMode, Grid, Mousewheel, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore from 'swiper';

SwiperCore.use([Autoplay]);
gsap.registerPlugin(ScrollTrigger, CSSPlugin, CSSRulePlugin);

// Generate unique ID for each component instance
let instanceCounter = 0;

const Partners = ({ data, version, award, mainIndex }) => {
	const scrollInstanceRef = useShowSetting();
	const sectionRef = useRef(null);
	const containerRef = useRef(null);
	const observerRef = useRef(null);
	const [isInView, setIsInView] = useState(false);
	const [swiperInstance, setSwiperInstance] = useState(null);
	const [animationComplete, setAnimationComplete] = useState(false);
	const [componentId] = useState(() => `partners-${++instanceCounter}`);

	const { ref, inView } = useInView({
		triggerOnce: true,
		threshold: 0.5,
	});

	// Memoize the split result to prevent unnecessary recalculations
	const result = useMemo(() => {
		const bigText = data?.section_data?.description || '';
		return bigText.split(',');
	}, [data?.section_data?.description]);

	// Memoize the subtitle to prevent unnecessary re-renders
	const subtitle = useMemo(() => data?.section_data?.subtitle, [data?.section_data?.subtitle]);

	// Memoize the short description to prevent unnecessary re-renders
	const shortDesc = useMemo(() => data?.section_data?.short_desc, [data?.section_data?.short_desc]);

	// Memoize the award items to prevent unnecessary re-renders
	const awardItems = useMemo(() => (
		data?.posts?.list?.map((e, index) => (
			<SwiperSlide key={index}>
				<Col md={12} className={'fade-up p-0'}>
				<AwardSingle
					link={false}
					img={e?.images}
					title={e?.data?.title}
					description={e?.data?.subtitle}
				/>
			</Col>
			</SwiperSlide>
		))
	), [data?.posts?.list]);

	// Memoize the partner images to prevent unnecessary re-renders
	const partnerImages = useMemo(() => (
		data?.images?.list?.map((e, index) => (
			<SwiperSlide key={index}>
				<Col md={12} className={`p-0 ${index+1}`} key={index}>
				<div className="single-partners-image">
					<div className="image-overlay"></div>
					<div className="image-wrapper">
						<img
							src={e?.full_path}
							alt={e?.short_title || ''}
							loading="lazy"
						/>

					</div>
				</div>
			</Col>
			</SwiperSlide>
		))
	), [data?.images?.list]);

	useEffect(() => {
		if (!containerRef.current) return;

		// Wait for content to be visible after preloader
		const checkVisibility = () => {
			const isVisible = containerRef.current.offsetParent !== null;
			return isVisible;
		};

		if (!checkVisibility()) {
			// If content is not visible, wait and retry
			const retryInterval = setInterval(() => {
				if (checkVisibility()) {
					clearInterval(retryInterval);
					initializeAnimation();
				}
			}, 100);
			return () => clearInterval(retryInterval);
		}

		function initializeAnimation() {
			const items = containerRef.current.querySelectorAll('.single-partners-image');

			// Only proceed if items exist
			if (items.length === 0) return;

		const imageWrappers = containerRef.current.querySelectorAll('.image-wrapper');
		const images = containerRef.current.querySelectorAll('.image-wrapper img');
		const overlays = containerRef.current.querySelectorAll('.image-overlay');

		// Clean up any existing ScrollTriggers
		ScrollTrigger.getAll().forEach(trigger => {
			if (trigger.trigger === containerRef.current) {
				trigger.kill();
			}
		});

		// Initial setup - avoid transform conflicts
		gsap.set(items, {
			opacity: 0,
			y: 40
		});

		gsap.set(imageWrappers, {
			scale: 0.8
		});

		gsap.set(images, {
			scale: 1.2,
			filter: 'blur(4px)'
		});

		gsap.set(overlays, {
			scaleX: 1,
			transformOrigin: 'left center'
		});

		// Create timeline for reveal animation with faster viewport detection
		const tl = gsap.timeline({
			scrollTrigger: {
				trigger: containerRef.current,
				start: 'top 95%', // Start earlier when element is 95% in viewport
				end: 'bottom 10%',
				toggleActions: 'play none none none',
				refreshPriority: 1, // Higher priority for faster execution
				fastScrollEnd: true, // Optimize for fast scrolling
				preventOverlaps: true, // Prevent animation overlaps
				onStart: () => {
					setIsInView(true);
					setAnimationComplete(false);
					// Pause swiper autoplay during reveal animation
					if (swiperInstance?.autoplay) {
						swiperInstance.autoplay.stop();
					}
				},
				onComplete: () => {
					// Mark animation as complete and resume swiper
					setAnimationComplete(true);
					setIsInView(false);
					// Resume swiper autoplay immediately after reveal animation completes
					if (swiperInstance?.autoplay) {
						swiperInstance.autoplay.start();
					}
				}
			},
		});

		// Animate each item with even faster, more efficient timing
		items.forEach((item, index) => {
			const imageWrapper = item.querySelector('.image-wrapper');
			const img = item.querySelector('.image-wrapper img');
			const overlay = item.querySelector('.image-overlay');

			// Container reveal - ultra fast timing
			tl.to(item, {
				opacity: 1,
				y: 0,
				duration: 0.25,
				ease: 'power1.out'
			}, index * 0.03)
				// Image wrapper scale - simultaneous with container
				.to(imageWrapper, {
					scale: 1,
					duration: 0.25,
					ease: 'power1.out'
				}, index * 0.03)
				// Overlay reveal - faster transition
				.to(overlay, {
					scaleX: 0,
					duration: 0.3,
					ease: 'power1.inOut',
					transformOrigin: 'right center'
				}, index * 0.03 + 0.03)
				// Image enhancement - instant blur removal
				.to(img, {
					scale: 1,
					filter: 'blur(0px)',
					duration: 0.3,
					ease: 'power1.out'
				}, index * 0.03 + 0.05);
		});

			return () => {
				ScrollTrigger.getAll().forEach(trigger => {
					if (trigger.trigger === containerRef.current) {
						trigger.kill();
					}
				});
				tl.kill();
			};
		}

		// Initialize the animation
		initializeAnimation();

		return () => {
			// Cleanup function
			ScrollTrigger.getAll().forEach(trigger => {
				if (trigger.trigger === containerRef.current) {
					trigger.kill();
				}
			});
		};
	}, [data?.images?.list, swiperInstance]);

	// Initialize swiper autoplay when component mounts
	useEffect(() => {
		if (swiperInstance && animationComplete) {
			// Start autoplay only after animation is complete
			setTimeout(() => {
				if (swiperInstance?.autoplay) {
					swiperInstance.autoplay.start();
				}
			}, 100);
		} else {
		}
	}, [swiperInstance, animationComplete]);
	useEffect(() => {
		if (!swiperInstance) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					// Only start autoplay if animation is complete and not currently revealing
					if (animationComplete && !isInView) {
						if (swiperInstance?.autoplay) {
							swiperInstance.autoplay.start();
						}
					}
				} else {
					if (swiperInstance?.autoplay) {
						swiperInstance.autoplay.stop();
					}
				}
			},
			{
				threshold: 0.1, // Lower threshold for earlier detection
				rootMargin: '50px 0px' // Start detection 50px before element enters viewport
			}
		);

		const currentContainer = containerRef.current;

		if (currentContainer) {
			observer.observe(currentContainer);
		}

		return () => {
			if (currentContainer) {
				observer.unobserve(currentContainer);
			}
			observer.disconnect();
		};
	}, [swiperInstance, isInView, animationComplete]);

	// Memoize the mousewheel config to prevent unnecessary re-renders
	const mousewheelConfig = useMemo(() => ({
		releaseOnEdges: true,
		sensitivity: 1.5,
		thresholdDelta: 1.5,
		thresholdTime: 5,
		eventsTarget: `.partners-${mainIndex}`,
	}), [mainIndex]);
	// Memoize the container style to prevent unnecessary re-renders


	return (
		<StyledPartners ref={sectionRef} data-scroll-section className={`partners partners-${mainIndex}`}>
			<Container>
				<Row className={'mb80'}>
					<Col md={4}>
						{subtitle && (
							<p data-scroll data-scroll-repeat className={'subtitle-ygen'}>
								{subtitle}
							</p>
						)}
					</Col>
					<Col md={8}>
						{shortDesc && (
							<Title
								tag={'h2'}
								animClass={'fade-up'}
								text={shortDesc}
								color={'#152637'}
							/>
						)}
					</Col>
				</Row>
			</Container>
			<Container className={'partners-images'} ref={containerRef}>
				<Row>
					{award ?
						<>
							<Col md={12}>
								<Swiper
									slidesPerView={4}
									slidesPerGroup={1}
									spaceBetween={15}
									onSwiper={setSwiperInstance}
									loop={true}
									autoplay={true}
									speed={1000}
									mousewheel={mousewheelConfig}
									pagination={{
										clickable: true,
									}}
								navigation={{
									prevEl: `.partner_prev_${componentId}`,
									nextEl: `.partner_next_${componentId}`,
								}}
									freeMode={false}
									breakpoints={{
										320: {
											slidesPerView: 2,

										},
										768: {
											slidesPerView: 3,
											spaceBetween: 15,
										},
										1024: {
											slidesPerView: 4,
											spaceBetween: 15,
										},
									}}
									modules={[Navigation,Pagination,Mousewheel, Autoplay]}
									className="about-slider_mask w-slider-mask"
								>
									{data?.posts?.list?.length > 0 && awardItems}
								</Swiper>
							</Col>


						</>
						: (
						<Col md={12}>
							<Swiper
								slidesPerView={7}
								grid={{
									rows: 3,
									fill: 'row', // Important for correct row filling
								}}
								slidesPerGroup={7}
								spaceBetween={25}
								onSwiper={setSwiperInstance}
								loop={true}
								autoplay={{
									delay: 2500,
									pauseOnMouseEnter: true,
									waitForTransition: true,
									stopOnLastSlide: false
								}}
								loopAddBlankSlides={true}
								loopAdditionalSlides={2}
								cssMode={false}
								speed={1000}
								pagination={{
									clickable: true
								}}
								navigation={{
									prevEl:  `.partner_prev_${componentId}`,
									nextEl:  `.partner_next_${componentId}`,
								}}
								freeMode={false}
								breakpoints={{
									320: {
										slidesPerView: 3,
										spaceBetween: 15,
										freeMode: true,
										speed: 2000,
										loop: true,
										cssMode: false,
										loopAdditionalSlides: 2,
										grid: {
											rows: 2,
											fill: 'row', // Important for correct row filling
										}

									},
									768: {
										slidesPerView: 4,
										spaceBetween: 15,
										grid: {
											rows: 3,
											fill: 'row', // Important for correct row filling
										}
									},
									1024: {
										slidesPerView: 7,
										spaceBetween: 25,
										grid: {
											rows: 3,
											fill: 'row', // Important for correct row filling
										}
									},
								}}
								modules={[Navigation, Grid, FreeMode, Autoplay]}
								className="about-slider_mask w-slider-mask"
							>
								{data?.images?.list?.length > 0 && partnerImages}
							</Swiper>
						</Col>
					)}
				</Row>
			</Container>
			{!award && (
				<>
					<div
						className="fix-right animated-right"
						data-scroll
						data-scroll-direction={'horizontal'}
						data-scroll-speed="10"
					>
						<LargeHeading
							textColor={'rgba(21, 38, 55, 0.03)'}
							text={result?.[0] || 'Industry'}
						/>
					</div>
					{result?.[1] && (
						<div
							className="fix-left animated-left"
							data-scroll
							data-scroll-direction={'horizontal animated-left'}
							data-scroll-speed="-10"
						>
							<LargeHeading
								textColor={'rgba(21, 38, 55, 0.03)'}
								text={result[1]}
							/>
						</div>
					)}
				</>
			)}
		</StyledPartners>
	);
};


export default React.memo(Partners);
